import type { SortingState, VisibilityState } from '@tanstack/react-table';

/**
 * Single source of truth for all columns in the Vorgänge tables.
 *
 * Pinned columns are fixed at the start of the table and are not exposed in
 * the column settings modal (they cannot be hidden or reordered). The
 * `user-controllable` columns are what the user sees in the modal.
 *
 * Extending this list automatically:
 *  - adds the column to the settings modal,
 *  - makes it visible by default for existing users (reconciled on hydrate),
 *  - keeps it in the default order (order in this array = default order).
 */
export interface VorgangColumnMeta {
  id: string;
  label: string;
  /**
   * Pinned columns render at the beginning of the table in the order they
   * appear here. They cannot be hidden, reordered, or sorted.
   */
  pinned?: 'start';
}

export const VORGANG_COLUMN_META: readonly VorgangColumnMeta[] = [
  { id: 'select', label: 'Auswahl', pinned: 'start' },
  { id: 'actions', label: 'Aktion', pinned: 'start' },
  { id: 'status', label: 'Status' },
  { id: 'currentStep', label: 'Aktueller Prozessschritt' },
  { id: 'createdAt', label: 'Erstellt' },
  { id: 'auftragsnummer', label: 'Auftrag' },
  { id: 'vertragsnummer', label: 'Vertrag' },
  { id: 'externeId', label: 'Event' },
  { id: 'externerName', label: 'Event Name' },
  { id: 'error', label: 'Fehler' },
  { id: 'fehlertyp', label: 'Fehlertyp' },
  { id: 'dokumentTyp', label: 'Dokument' },
  { id: 'archivierungsstatus', label: 'Archivierung' },
  { id: 'unterdrueckung', label: 'Unterdrückung' },
  { id: 'lockedBy', label: 'Gesperrt' },
] as const;

export const PINNED_COLUMN_IDS: readonly string[] = VORGANG_COLUMN_META.filter(
  (c) => c.pinned === 'start',
).map((c) => c.id);

export const USER_CONTROLLABLE_COLUMN_IDS: readonly string[] =
  VORGANG_COLUMN_META.filter((c) => !c.pinned).map((c) => c.id);

export const isPinnedColumn = (id: string): boolean =>
  PINNED_COLUMN_IDS.includes(id);

export const getColumnLabel = (id: string): string =>
  VORGANG_COLUMN_META.find((c) => c.id === id)?.label ?? id;

export interface VorgangColumnSettings {
  /** User-controllable column ids in their current order. Pinned ids are not stored here. */
  columnOrder: string[];
  /** Visibility map keyed by column id. Missing ids default to visible. */
  columnVisibility: VisibilityState;
  /** Persisted TanStack sorting state. Empty array = no active sort. */
  sorting: SortingState;
}

export const getDefaultColumnSettings = (): VorgangColumnSettings => ({
  columnOrder: [...USER_CONTROLLABLE_COLUMN_IDS],
  columnVisibility: Object.fromEntries(
    USER_CONTROLLABLE_COLUMN_IDS.map((id) => [id, true]),
  ),
  sorting: [],
});

/**
 * Build the final column order passed to TanStack Table, with pinned columns
 * prepended in their canonical order.
 */
export const buildFullColumnOrder = (
  userOrder: readonly string[],
): string[] => [...PINNED_COLUMN_IDS, ...userOrder];

/**
 * Reconcile persisted settings against the current `VORGANG_COLUMN_META`:
 *  - drop unknown / pinned ids from the order,
 *  - append newly added user-controllable ids at the end (visible by default),
 *  - drop unknown / pinned ids from visibility,
 *  - drop unknown / pinned ids from sorting.
 *
 * Guarantees callers always get a valid settings object matching the current
 * meta definition, which keeps the app forward-compatible when columns are
 * added or removed in future stories.
 */
export const reconcileColumnSettings = (
  raw: Partial<VorgangColumnSettings> | null | undefined,
): VorgangColumnSettings => {
  const known = new Set(USER_CONTROLLABLE_COLUMN_IDS);

  const storedOrder = Array.isArray(raw?.columnOrder) ? raw.columnOrder : [];
  const filteredOrder = storedOrder.filter(
    (id): id is string => typeof id === 'string' && known.has(id),
  );
  const seen = new Set(filteredOrder);
  const missing = USER_CONTROLLABLE_COLUMN_IDS.filter((id) => !seen.has(id));
  const columnOrder = [...filteredOrder, ...missing];

  const rawVisibility =
    raw?.columnVisibility && typeof raw.columnVisibility === 'object'
      ? raw.columnVisibility
      : {};
  const columnVisibility: VisibilityState = {};
  for (const id of USER_CONTROLLABLE_COLUMN_IDS) {
    columnVisibility[id] = rawVisibility[id] !== false;
  }

  const storedSorting = Array.isArray(raw?.sorting) ? raw.sorting : [];
  const sorting: SortingState = storedSorting.filter(
    (s): s is { id: string; desc: boolean } =>
      !!s &&
      typeof s === 'object' &&
      typeof (s as { id?: unknown }).id === 'string' &&
      known.has((s as { id: string }).id) &&
      typeof (s as { desc?: unknown }).desc === 'boolean',
  );

  return { columnOrder, columnVisibility, sorting };
};
